import React, { useEffect, useState } from 'react';
import { getFirestore, collection, doc, query, where, getDocs, getDoc } from "firebase/firestore";

interface Translation {
  es?: string;
  hi?: string;
  ar?: string;
  ja?: string;
  ur?: string;
  zh?: string;
  fr?: string;
  tr?: string;
  id?: string;
  bn?: string;
  th?: string;
  it?: string;
  vi?: string;
  ru?: string;
  pt?: string;
  [key: string]: string | undefined;
}

interface Phrase {
  text: string;
  translation: Translation;
}

interface LyricsData {
  songId: string;
  artist: string;
  name: string;
  translation: Translation;
  phrases: Phrase[];
}

interface SongData {
  yt: string[];
  ytIndex: number;
}

interface LyricsProps {
  currentSong: number;
  currentLanguage: string;
  translate: boolean;
  top10Songs: Song[];
}

interface Song {
  id: string;
  name: string;
  artist: string;
  position: number;
  yt: string[];
  ytIndex: number;
  translation: Record<string, string>;
}

const Lyrics: React.FC<LyricsProps> = ({ currentSong, currentLanguage, translate, top10Songs }) => {
  const db = getFirestore();
  const [lyricsData, setLyricsData] = useState<LyricsData | null>(null);
  const [songData, setSongData] = useState<SongData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch lyrics data
        console.log('top10Songs length', top10Songs?.length);
        console.log('currentSong:', currentSong);
        const songEntry = top10Songs && top10Songs[currentSong];
        if (!songEntry) {
          throw new Error('Song entry not available for currentSong');
        }
        console.log('Fetching lyrics for song ID:', songEntry.id);
        const lyricsDocRef = collection(db, 'lyrics');
        const q = query(lyricsDocRef, where("songId", "==", songEntry.id));
        const lyricsDocSnap = await getDocs(q);
        if (lyricsDocSnap.empty) {
          throw new Error('Lyrics not found');
        }
        const lyrics = lyricsDocSnap.docs[0].data() as LyricsData;
        setLyricsData(lyrics);

        // Fetch song data for YouTube video
        /*const songDocRef = doc(db, 'songs', currentSong);
        const songDocSnap = await getDoc(songDocRef);

        if (!songDocSnap.exists()) {
          throw new Error('Song not found');
        }

        const song = songDocSnap.data() as SongData;
        setSongData(song);*/
        setSongData({ yt: songEntry.yt, ytIndex: songEntry.ytIndex });

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };

    // Only fetch when currentSong is a valid number and top10Songs is ready
    if (typeof currentSong === 'number' && top10Songs && top10Songs.length > 0 && currentSong >= 0 && currentSong < top10Songs.length) {
      fetchData();
    } else {
      console.log('Lyrics: skipping fetch — currentSong or top10Songs not ready', { currentSong, top10SongsLength: top10Songs?.length });
      setLyricsData(null);
      setSongData(null);
      setLoading(false);
    }
  }, [currentSong, top10Songs]);

  const speakText = (text: string) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.9; // Slightly slower for language learners
      window.speechSynthesis.speak(utterance);
    } else {
      console.warn('Text-to-speech not supported in this browser');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-white text-xl">Loading...{currentSong}</div>
      </div>
    );
  }

  if (error || !lyricsData || !songData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-400 text-xl">Error: {error || 'Data not available'}</div>
      </div>
    );
  }

  const youtubeId = songData.yt[songData.ytIndex];
  const songTranslation = translate && lyricsData.translation[currentLanguage];

  return (
    <div
      className="min-h-screen py-8 px-4 sm:px-6 lg:px-8"
      style={{
        maxHeight: '100vh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
        color: '#0b3d91',
        paddingTop: '84px'
      }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Song Title Section */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold"
              style={{ color: '#0b3d91', letterSpacing: '-0.02em', lineHeight: 1.05 }}
            >
              {lyricsData.name}
            </h1>
            <button
              onClick={() => speakText(lyricsData.name)}
              className="flex-shrink-0 bg-gradient-to-br from-white/18 to-white/8 hover:from-white/25 hover:to-white/12 text-white rounded-full p-3 transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/50"
              aria-label="Play song title"
              style={{ width: 'clamp(44px, 6vw, 72px)', height: 'clamp(44px, 6vw, 72px)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 6 }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.4))' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                />
              </svg>
            </button>
          </div>

          {songTranslation && (
            <p className="text-xl sm:text-2xl mb-2" style={{ color: 'rgba(11,61,145,0.8)' }}>
              {songTranslation}
            </p>
          )}

          <p className="text-lg sm:text-xl" style={{ color: 'rgba(11,61,145,0.78)', marginTop: 6 }}>
            {lyricsData.artist}
          </p>
        </div>

        {/* YouTube Video */}
        <div className="mb-2 sm:mb-4">
          <div
            className="w-full"
            style={{
              height: '40vh',
              maxHeight: '40vh',
              overflow: 'hidden',
              maxWidth: '900px',
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <iframe
              style={{ width: '100%', height: '100%', borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}
              src={`https://www.youtube.com/embed/${youtubeId}`}
              title={lyricsData.name}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>

        {/* Lyrics Section */}
        <div className="space-y-4">
          {lyricsData.phrases.map((phrase, index) => {
            const phraseTranslation = translate && phrase.translation[currentLanguage];

            return (
              <div
                key={index}
                className="bg-white/6 backdrop-blur-sm rounded-lg p-3 sm:p-4 transition-all duration-200 hover:bg-white/12"
                style={{ marginLeft: '0.75rem', display: 'flex', alignItems: 'flex-start', gap: 12 }}
              >
                <button
                  onClick={() => speakText(phrase.text)}
                  className="flex-shrink-0 bg-gradient-to-br from-white/18 to-white/8 hover:from-white/25 hover:to-white/12 text-white rounded-full p-3 transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/50"
                  aria-label={`Play phrase ${index + 1}`}
                  style={{ width: 'clamp(44px, 6vw, 72px)', height: 'clamp(44px, 6vw, 72px)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 6 }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.4))' }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    />
                  </svg>
                </button>

                <div style={{ flex: 1 }}>
                  <p style={{ color: '#0b3d91', letterSpacing: '-0.01em', lineHeight: 1.15, margin: 0, fontWeight: 700, fontSize: 'clamp(1.5rem, 5vw, 2.75rem)' }}>
                    {phrase.text}
                  </p>

                  {phraseTranslation && (
                    <p style={{ color: 'rgba(11,61,145,0.78)', marginTop: '0.5rem', fontStyle: 'italic', fontSize: 'clamp(1rem, 3.5vw, 2.25rem)' }}>
                      {phraseTranslation}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Lyrics;
